/**
 * GovMap JavaScript API — load script, initialize hidden map with PARCEL_ALL,
 * and run searchAndLocate for parcel verification.
 * @see https://api.govmap.gov.il/docs/javascript-functions/search-and-locate
 */

const GOVMAP_SCRIPT_URL = "https://www.govmap.gov.il/govmap/api/govmap.api.js";
const HIDDEN_MAP_CONTAINER_ID = "btax-haklai-govmap-host";
const SEARCH_AND_LOCATE_TIMEOUT_MS = 12000;

let scriptLoadPromise = null;
let mapInitPromise = null;

function loadGovMapScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("GovMap requires a browser environment"));
  }
  if (window.govmap) return Promise.resolve();

  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GOVMAP_SCRIPT_URL}"]`);
    if (existing) {
      if (window.govmap) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("GovMap script failed")));
      return;
    }
    const script = document.createElement("script");
    script.src = GOVMAP_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("GovMap script failed"));
    document.body.appendChild(script);
  });

  return scriptLoadPromise;
}

function ensureHiddenMapContainer() {
  let el = document.getElementById(HIDDEN_MAP_CONTAINER_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = HIDDEN_MAP_CONTAINER_ID;
    el.setAttribute("aria-hidden", "true");
    el.style.cssText =
      "position:fixed;width:2px;height:2px;overflow:hidden;opacity:0;pointer-events:none;left:0;top:0;z-index:-1;";
    document.body.appendChild(el);
  }
  return el;
}

/**
 * Initializes a minimal map instance (PARCEL_ALL only) required for searchAndLocate.
 * Safe to call multiple times — reuses the same promise.
 */
export function ensureGovMapReady(apiToken) {
  if (!apiToken || !String(apiToken).trim()) {
    return Promise.reject(new Error("MISSING_GOVMAP_TOKEN"));
  }

  return loadGovMapScript().then(
    () =>
      new Promise((resolve, reject) => {
        if (mapInitPromise) {
          mapInitPromise.then(resolve).catch(reject);
          return;
        }

        const { govmap } = window;
        if (!govmap || typeof govmap.createMap !== "function") {
          reject(new Error("GovMap API unavailable"));
          return;
        }

        ensureHiddenMapContainer();

        mapInitPromise = new Promise((mapResolve, mapReject) => {
          const settled = { done: false };
          const settleOk = () => {
            if (settled.done) return;
            settled.done = true;
            mapResolve();
          };
          const settleErr = (err) => {
            if (settled.done) return;
            settled.done = true;
            mapReject(err);
          };

          try {
            govmap.createMap(HIDDEN_MAP_CONTAINER_ID, {
              token: apiToken,
              layers: ["PARCEL_ALL"],
              background: "11",
              showXY: false,
              identifyOnClick: false,
              zoomButtons: false,
              bgButton: false,
              layersMode: 1,
              isEmbeddedToggle: false,
              onLoad: settleOk,
              onError: (e) => settleErr(e || new Error("GovMap map error"))
            });
          } catch (e) {
            settleErr(e);
            return;
          }

          window.setTimeout(() => {
            if (!settled.done) settleOk();
          }, 4000);
        });

        mapInitPromise.then(resolve).catch(reject);
      })
  );
}

function extractResultRows(response) {
  if (response == null) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.results)) return response.results;
  return [];
}

function searchAndLocateWithTimeout(govmap, params) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error("GovMap search timed out"));
    }, SEARCH_AND_LOCATE_TIMEOUT_MS);

    const settleOk = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(value);
    };

    const settleErr = (error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      reject(error);
    };

    try {
      Promise.resolve(govmap.searchAndLocate(params)).then(settleOk).catch(settleErr);
    } catch (error) {
      settleErr(error);
    }
  });
}

/**
 * Normalizes searchAndLocate rows: Values[0] = gush, Values[1] = helka (parcel).
 */
export function rowsToParcelPairs(rows) {
  const out = [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const values = row.Values || row.values;
    if (!Array.isArray(values) || values.length < 2) continue;
    const gush = Number(values[0]);
    const helka = Number(values[1]);
    if (!Number.isFinite(gush) || !Number.isFinite(helka)) continue;
    out.push({ gush, helka, objectId: row.ObjectId ?? row.objectId });
  }
  return out;
}

/**
 * Normalizes address rows returned by addressToLotParcel:
 * Values[0] = city, Values[1] = street, Values[2] = house number.
 * GovMap docs example:
 * https://api.govmap.gov.il/docs/javascript-functions/search-and-locate
 */
export function rowsToAddressCandidates(rows) {
  const out = [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const values = row.Values || row.values;
    if (!Array.isArray(values) || values.length < 3) continue;

    const city = values[0] == null ? "" : String(values[0]).trim();
    const street = values[1] == null ? "" : String(values[1]).trim();
    const houseNumber = values[2] == null ? "" : String(values[2]).trim();

    if (!street || !city) continue;

    out.push({
      street,
      city,
      houseNumber,
      objectId: row.ObjectId ?? row.objectId,
      settlementCode: row.settlementCode ?? null,
      streetCode: row.streetCode ?? null
    });
  }
  return out;
}

/**
 * Address → one or more (gush, helka).
 * GovMap's documented enum for this direction is counterintuitive:
 * lotParcelToAddress is used with an `address` string.
 */
export function searchLotParcelFromAddress(apiToken, address) {
  const trimmed = String(address || "").trim();
  if (!trimmed) return Promise.resolve([]);

  return ensureGovMapReady(apiToken).then(() => {
    const { govmap } = window;
    const params = {
      type: govmap.locateType.lotParcelToAddress,
      address: trimmed
    };
    return searchAndLocateWithTimeout(govmap, params).then((response) => {
      const rows = extractResultRows(response);
      return rowsToParcelPairs(rows);
    });
  });
}

/**
 * Gush + helka → one or more address candidates.
 * GovMap's documented enum for this direction is counterintuitive:
 * addressToLotParcel is used with `lot` and `parcel`.
 */
export function searchAddressesFromLotParcel(apiToken, lot, parcel) {
  const lotNum = Number(lot);
  const parcelNum = Number(parcel);
  if (!Number.isFinite(lotNum) || lotNum <= 0 || !Number.isFinite(parcelNum) || parcelNum <= 0) {
    return Promise.resolve([]);
  }

  return ensureGovMapReady(apiToken).then(() => {
    const { govmap } = window;
    const params = {
      type: govmap.locateType.addressToLotParcel,
      lot: lotNum,
      parcel: parcelNum
    };
    return searchAndLocateWithTimeout(govmap, params).then((response) => {
      const rows = extractResultRows(response);
      return rowsToAddressCandidates(rows);
    });
  });
}

/**
 * Gush + helka → verify parcel exists (addressToLotParcel).
 */
export function verifyLotParcelWithGovMap(apiToken, lot, parcel) {
  const lotNum = Number(lot);
  const parcelNum = Number(parcel);
  if (!Number.isFinite(lotNum) || lotNum <= 0 || !Number.isFinite(parcelNum) || parcelNum <= 0) {
    return Promise.resolve(false);
  }

  return ensureGovMapReady(apiToken).then(() => {
    const { govmap } = window;
    const params = {
      type: govmap.locateType.addressToLotParcel,
      lot: lotNum,
      parcel: parcelNum
    };
    return searchAndLocateWithTimeout(govmap, params).then((response) => {
      const rows = extractResultRows(response);
      return rows.length > 0;
    });
  });
}
